'use client'

import Image from 'next/image'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import { containerClassName } from '@/components/Container'
import { PromotionCard } from '@/components/Promotions/PromotionCard'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { useAmbientConfetti } from '@/hooks/useAmbientConfetti'
import { useHydratedNow } from '@/hooks/useHydratedNow'
import { useLocale } from '@/lib/i18n/useLocale'
import { useTemplatePathMap } from '@/lib/pages/LocalizedPagePathsProvider'
import { useMessages } from '@/lib/i18n/useMessages'
import { getPromotionCardProps } from '@/lib/promotions/promotionCard'
import type { PromotionListItem } from '@/lib/promotions/promotionsQuery'
import { cn } from '@/utilities/ui'

const promotionCarouselControlClassName =
  'static top-auto size-10 translate-none rounded-full border-[1.5px] border-white/95 bg-black/50 text-white hover:bg-black/60 hover:text-white disabled:opacity-40'

const promotionSlideClassName =
  'w-full max-w-[40rem] basis-[calc((100%-1.25rem)/1.25)] pl-5 md:basis-[calc((100%-2.5rem)/2.25)] lg:max-w-[32rem] lg:basis-[calc((100%-2.5rem)/2.25)]'

const promotionCarouselInsetClassName = 'pl-4 lg:pl-0'

const promotionCarouselBleedClassName =
  'relative ml-[calc(50%-50vw)] w-screen max-w-[100vw] lg:ml-0 lg:w-[calc(100%+max(1rem,calc((100vw-80rem)/2+1rem)))] lg:max-w-none'

const promotionLastSlideClassName =
  'mr-5 lg:mr-[max(1rem,calc((100vw-80rem)/2+1rem))]'

const promotionDesktopScrollMediaQuery = '(min-width: 64rem)'

const defaultMascotImage = '/media/homepage/promotions/hero-mascot-254c14.png'

function isDesktopPromotionScroll(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia(promotionDesktopScrollMediaQuery).matches
  )
}

type BestDealPromotionsProps = {
  mascotImage?: string
  mascotImageMirrored?: boolean
  promotions: PromotionListItem[]
}

export const BestDealPromotions: React.FC<BestDealPromotionsProps> = ({
  mascotImage = defaultMascotImage,
  mascotImageMirrored = true,
  promotions,
}) => {
  const locale = useLocale()
  const templatePaths = useTemplatePathMap()
  const messages = useMessages()
  const copy = messages.landing.bestDealPromotions
  const gridCopy = messages.promotions.grid
  const sectionRef = useRef<HTMLElement>(null)
  const [carouselApi, setCarouselApi] = useState<CarouselApi>()
  const offerNowMs = useHydratedNow()

  useAmbientConfetti(sectionRef)

  const labels = useMemo(
    () => ({
      imageAltFallback: gridCopy.imageAltFallback,
      offerEndsInDaysHours: gridCopy.offerEndsInDaysHours,
      offerEndsInHoursMinutes: gridCopy.offerEndsInHoursMinutes,
      offerEndsInMonthsDays: gridCopy.offerEndsInMonthsDays,
      offerEndsInYearsMonths: gridCopy.offerEndsInYearsMonths,
      offerEndsSoon: gridCopy.offerEndsSoon,
      permanentOffer: gridCopy.permanentOffer,
      readMore: gridCopy.readMore,
      readMoreAriaLabel: gridCopy.readMoreAriaLabel,
    }),
    [gridCopy],
  )

  const handleCarouselNext = useCallback(() => {
    if (!carouselApi) {
      return
    }

    const currentSnap = carouselApi.selectedScrollSnap()
    const lastSnap = carouselApi.scrollSnapList().length - 1

    if (isDesktopPromotionScroll() && currentSnap === 0 && lastSnap > 0) {
      carouselApi.scrollTo(lastSnap)
      return
    }

    carouselApi.scrollNext()
  }, [carouselApi])

  const handleCarouselPrev = useCallback(() => {
    if (!carouselApi) {
      return
    }

    const currentSnap = carouselApi.selectedScrollSnap()
    const lastSnap = carouselApi.scrollSnapList().length - 1

    if (isDesktopPromotionScroll() && currentSnap === lastSnap && lastSnap > 0) {
      carouselApi.scrollTo(0)
      return
    }

    carouselApi.scrollPrev()
  }, [carouselApi])

  if (!promotions.length) {
    return null
  }

  return (
    <section
      ref={sectionRef}
      aria-labelledby="best-deal-promotions-heading"
      className="relative isolate scroll-mt-8 overflow-x-clip bg-pink-wash"
      id="promotions"
    >
      <Carousel
        aria-label={copy.carouselAriaLabel}
        className="relative z-10 w-full"
        opts={{ align: 'start', containScroll: 'trimSnaps' }}
        setApi={setCarouselApi}
      >
        <div className={cn(containerClassName, 'pb-0 pt-0 lg:pt-20')}>
          <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-10 pb-16 lg:pb-6">
            <div className="w-full shrink-0 lg:max-w-[26rem] lg:self-start">
              <div className="flex flex-row items-end justify-between gap-3 lg:flex-col lg:items-start lg:gap-0">
                <h2
                  className="min-w-0 flex-1 text-title font-display text-[2.5rem] font-semibold leading-none tracking-[-0.04em] md:text-[4rem] md:leading-[4rem] lg:flex-none"
                  id="best-deal-promotions-heading"
                >
                  <span className="block text-bluetext">{copy.titleLine1}</span>
                  <span className="block text-[#B10FBD]">{copy.titleLine2}</span>
                </h2>

                <div className="relative h-[10.5rem] w-[8rem] shrink-0 sm:h-[12.5rem] sm:w-[9.5rem] lg:mt-10 lg:h-[26rem] lg:w-full lg:max-w-[26rem]">
                  <div
                    aria-hidden
                    className="absolute bottom-0 left-1/2 h-[1.25rem] w-[6.5rem] -translate-x-1/2 rounded-[50%] bg-black/5 sm:h-[1.5rem] sm:w-[7.5rem] lg:h-[2.4375rem] lg:w-[21.25rem]"
                  />
                  <Image
                    alt={copy.mascotImageAlt}
                    className={cn(
                      'object-contain object-bottom',
                      mascotImageMirrored && '-scale-x-100',
                    )}
                    fill
                    sizes="(max-width: 1024px) 9.5rem, 26rem"
                    src={mascotImage}
                  />
                </div>
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div
                className={cn(
                  'best-deal-promotions-carousel',
                  promotionCarouselBleedClassName,
                  promotionCarouselInsetClassName,
                )}
                data-best-deal-promotions-carousel
              >
                <CarouselContent className="-ml-5" role="list">
                  {promotions.map((promotion, index) => (
                    <CarouselItem
                      key={promotion.id}
                      className={cn(
                        promotionSlideClassName,
                        index === promotions.length - 1 && promotionLastSlideClassName,
                      )}
                      role="listitem"
                    >
                      <PromotionCard
                        {...getPromotionCardProps(promotion, locale, labels, {
                          layout: 'vertical',
                          nowMs: offerNowMs,
                          showLocationBadge: false,
                          templatePaths,
                        })}
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </div>

              {promotions.length > 1 ? (
                <div className="mt-6 flex items-center justify-end gap-3 pr-5 lg:pr-0">
                  <CarouselPrevious
                    aria-label={copy.carouselPrevious}
                    className={promotionCarouselControlClassName}
                    onClick={handleCarouselPrev}
                    size="icon"
                    variant="outline"
                  />
                  <CarouselNext
                    aria-label={copy.carouselNext}
                    className={promotionCarouselControlClassName}
                    onClick={handleCarouselNext}
                    size="icon"
                    variant="outline"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Carousel>
    </section>
  )
}
